import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

if (!supabaseUrl) {
  throw new Error('Missing REACT_APP_SUPABASE_URL environment variable. Please check your .env file.')
}

if (!supabaseAnonKey) {
  throw new Error('Missing REACT_APP_SUPABASE_ANON_KEY environment variable. Please check your .env file.')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Helper functions for common operations
export const auth = {
  signUp: (email, password) => supabase.auth.signUp({ email, password }),
  signIn: (email, password) => supabase.auth.signInWithPassword({ email, password }),
  signOut: () => supabase.auth.signOut(),
  getUser: () => supabase.auth.getUser(),
  onAuthStateChange: (callback) => supabase.auth.onAuthStateChange(callback)
}

export const db = {
  // Categories
  getCategories: () => supabase.from('categories').select('*').eq('active', true),
  getCategory: (id) => supabase.from('categories').select('*').eq('id', id).single(),
  createCategory: (category) => supabase.from('categories').insert(category).select(),
  updateCategory: (id, updates) => supabase.from('categories').update(updates).eq('id', id).select(),
  deleteCategory: (id) => supabase.from('categories').delete().eq('id', id),

  // Users
  getUsers: () => supabase.from('users').select('*'),
  getUser: (id) => supabase.from('users').select('*').eq('id', id).single(),
  getUserByEmail: (email) => supabase.from('users').select('*').eq('email', email).single(),
  createUser: (user) => supabase.from('users').insert(user).select(),
  updateUser: (id, updates) => supabase.from('users').update(updates).eq('id', id).select(),
  deleteUser: (id) => supabase.from('users').delete().eq('id', id),

  // Addresses
  getUserAddresses: (userId) => supabase.from('addresses').select('*').eq('user_id', userId),
  getAddress: (id) => supabase.from('addresses').select('*').eq('id', id).single(),
  createAddress: (address) => supabase.from('addresses').insert(address).select(),
  updateAddress: (id, updates) => supabase.from('addresses').update(updates).eq('id', id).select(),
  deleteAddress: (id) => supabase.from('addresses').delete().eq('id', id),

  // Products
  getProducts: () => supabase.from('products').select('*, categories(*)').eq('active', true),
  getProduct: (id) => supabase.from('products').select('*, categories(*)').eq('id', id).single(),
  getProductsByCategory: (categoryId) => supabase.from('products').select('*, categories(*)').eq('category_id', categoryId).eq('active', true),
  createProduct: (product) => supabase.from('products').insert(product).select(),
  updateProduct: (id, updates) => supabase.from('products').update(updates).eq('id', id).select(),
  deleteProduct: (id) => supabase.from('products').delete().eq('id', id),

  // Orders
  getOrders: () => supabase.from('orders').select('*'),
  getUserOrders: (userId) => supabase.from('orders').select('*').eq('user_id', userId).order('created_at', { ascending: false }),
  getOrder: (id) => supabase.from('orders').select('*').eq('id', id).single(),
  createOrder: (order) => supabase.from('orders').insert(order).select(),
  updateOrder: (id, updates) => supabase.from('orders').update(updates).eq('id', id).select(),
  deleteOrder: (id) => supabase.from('orders').delete().eq('id', id),

  // Order Items
  getOrderItems: (orderId) => supabase.from('order_items').select('*, products(*)').eq('order_id', orderId),
  createOrderItem: (item) => supabase.from('order_items').insert(item).select(),
  createOrderItems: (items) => supabase.from('order_items').insert(items).select(),
}

export const storage = {
  // Upload file to storage bucket
  uploadFile: (bucket, file, fileName) => supabase.storage.from(bucket).upload(fileName, file),

  // Get public URL for a file
  getPublicUrl: (bucket, fileName) => supabase.storage.from(bucket).getPublicUrl(fileName),

  // Delete file from storage bucket
  deleteFile: (bucket, fileName) => supabase.storage.from(bucket).remove([fileName]),

  // List files in a bucket
  listFiles: (bucket, path = '') => supabase.storage.from(bucket).list(path),
}